/**
 * Markdown do painel, portado de app.js:112-171 sem mudar uma regra.
 *
 * Não é um markdown completo de propósito: é exatamente o subconjunto que o
 * acervo produz (heading, lista, código, ênfase) e nada além. A parte que não
 * pode mudar é a âncora: o identificador de cada bloco é o hash do próprio
 * texto, então a mesma prosa gera a mesma âncora e a nota continua colada nela;
 * doc regerada com outro texto solta a nota, que reaparece em "sem âncora".
 * Trocar o algoritmo de hash aqui desancoraria todas as notas do acervo.
 */

/* eslint-disable no-control-regex -- o marcador de bloco de código é o \x00PRE<n>\x00
   do painel atual (app.js:119). É caractere de controle de propósito: ele não
   pode aparecer em markdown de verdade, e é justamente isso que o torna um
   marcador seguro para tirar o código do caminho das outras substituições. */

export type EspecieDeBloco = 'fact' | 'heading' | 'block';

export interface Bloco {
  /** hash do texto original do bloco; é a âncora que a nota guarda */
  id: string;
  especie: EspecieDeBloco;
  html: string;
  /** o markdown cru do bloco, que vai como anchor_quote na nota */
  citacao: string;
}

const esc = (s: string | null | undefined) => (s || '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** djb2, o mesmo de app.js:113 — mudar isto desancora as notas já escritas. */
export function chaveDoBloco(s: string): string {
  const t = String(s || '').replace(/\s+/g, ' ').trim();
  let h = 5381;
  for (let i = 0; i < t.length; i++) h = ((h << 5) + h + t.charCodeAt(i)) >>> 0;
  return h.toString(16).padStart(8, '0');
}

/**
 * A prosa saneada nem sempre traz linha em branco antes de um heading ou de uma
 * lista, então quebrar só em \n\n grudaria a seção toda num bloco: separa por
 * linha em branco, por heading, por bloco de código e por grupo de lista.
 */
function fatiar(src: string | null | undefined): { blocos: string[]; pres: string[] } {
  const pres: string[] = [];
  const cru = String(src || '').replace(/```(\w*)\n([\s\S]*?)```/g, (_m, _l, c: string) => {
    pres.push(c.replace(/\n$/, ''));
    return `\u0000PRE${pres.length - 1}\u0000`;
  });
  const blocos: string[] = [];
  let buf: string[] = [];
  let modo: 'ul' | 'p' | null = null;
  const fechar = () => {
    const t = buf.join('\n').trim();
    if (t) blocos.push(t);
    buf = [];
    modo = null;
  };
  for (const linha of cru.split('\n')) {
    if (!linha.trim()) { fechar(); continue; }
    if (/^#{1,6} /.test(linha) || /^\u0000PRE\d+\u0000$/.test(linha.trim())) {
      fechar();
      blocos.push(linha.trim());
      continue;
    }
    const m = /^\s*([-*]|\d+\.)\s/.test(linha) ? 'ul' : 'p';
    if (modo && modo !== m) fechar();
    modo = m;
    buf.push(linha);
  }
  fechar();
  return { blocos, pres };
}

function devolverPres(bloco: string, pres: string[]): string {
  return bloco.replace(/\u0000PRE(\d+)\u0000/g, (_m, i: string) => `\`\`\`\n${pres[+i]}\n\`\`\``);
}

function umBloco(bloco: string, pres: string[]): string {
  let h = esc(bloco)
    .replace(/^### (.*)$/gm, '<h3>$1</h3>')
    .replace(/^## (.*)$/gm, '<h2>$1</h2>')
    .replace(/^# (.*)$/gm, '<h1>$1</h1>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    // itálico sem estragar snake_case nem unit_key do tipo __decision__/0007
    .replace(/(^|[\s([{>—])_(?!_)([^\n]*?[^\s_])(?<!_)_(?![\w_])/g, '$1<em>$2</em>')
    .replace(/(^|[\s([{>—])\*(?!\*)([^\n*]+?)\*(?!\*)/g, '$1<em>$2</em>');
  h = h.replace(/ {2,}\n/g, '<br>\n');
  h = h.replace(/(^- .*(?:\n- .*)*)/gm, (m) => `<ul>${m.split('\n').map((l) => `<li>${l.replace(/^-\s*/, '')}</li>`).join('')}</ul>`);
  if (!/^\s*(<(h\d|ul|pre)|\u0000PRE)/.test(h)) h = `<p>${h.replace(/\n/g, ' ')}</p>`;
  return h.replace(/\u0000PRE(\d+)\u0000/g, (_m, i: string) => `<pre><code>${esc(pres[+i])}</code></pre>`);
}

/** Markdown para exibição simples (nota, resumo, decisão): sem âncora. */
export function md(src: string | null | undefined): string {
  if (!src) return '';
  const { blocos, pres } = fatiar(src);
  return blocos.map((b) => umBloco(b, pres)).join('\n');
}

/**
 * Markdown do corpo de um documento, bloco a bloco e com âncora.
 *
 * Devolve dados, e não HTML pronto: o botão "+" de cada bloco e a lista de notas
 * abaixo dele são componentes, e no painel atual eram HTML costurado à mão com
 * um ouvinte de clique no container.
 */
export function analisar(src: string | null | undefined): Bloco[] {
  const { blocos, pres } = fatiar(src);
  let secao = '';
  return blocos.map((b) => {
    const citacao = devolverPres(b, pres);
    const cabeca = /^#{1,6} /.test(citacao);
    if (cabeca) secao = citacao.replace(/^#+\s*/, '');
    return {
      id: chaveDoBloco(citacao),
      especie: (/^Fatos \(AST/.test(secao) ? 'fact' : cabeca ? 'heading' : 'block') as EspecieDeBloco,
      html: umBloco(b, pres),
      citacao,
    };
  });
}

/**
 * Tira do corpo a seção "Notas do time".
 *
 * Esse bloco existe para quem lê texto puro (CLI, MCP, copiar e colar); na web
 * as notas viram cartões. Corta só essa seção — o que vem depois dela, como os
 * resumos de sessão, tem de continuar visível.
 */
export function semNotasDoTime(src: string | null | undefined): string {
  return String(src || '').replace(/\n*## Notas do time[\s\S]*?(?=\n## |$)/, '');
}
